import { PrivateImage } from "@/components/PrivateMedia";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MapPin, Heart } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { optionLabel } from "@/lib/profileLabels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function MatchCard({ m }) {
  const { lang, t } = useI18n();
  const img = photoUrl(m.main_photo_url);
  return (
    <Link
      to={`/app/matches/${m.match_id}`}
      className="nvl-card overflow-hidden block transition-transform hover:-translate-y-0.5"
      data-testid={`match-card-${m.match_id}`}
    >
      {img ? <PrivateImage src={img} alt={m.display_name || ""} className="w-full aspect-[4/5] object-contain bg-black/5" /> : null}
      <div
        className="relative w-full flex items-end p-4"
        style={{
          backgroundColor: "hsl(var(--nvl-ink))",
          backgroundSize: "cover", backgroundPosition: "center",
          color: "hsl(var(--nvl-bg))",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: img ? "linear-gradient(180deg, transparent 45%, rgba(10,17,40,0.85))" : undefined }}
        />
        <div className="relative w-full">
          <div className="flex items-end justify-between gap-3">
            <h3 className="font-display text-xl leading-tight">
              {m.display_name}{m.age ? <span className="opacity-80">, {m.age}</span> : null}
            </h3>
            {m.compatibility?.confidence === "low" ? null : (
              <span className="text-xs" data-testid={`match-compat-${m.match_id}`}>
                {m.compatibility.score}%
              </span>
            )}
          </div>
          {m.city ? (
            <p className="mt-1 text-[11px] flex items-center gap-1 opacity-90">
              <MapPin size={10} /> {m.city}
            </p>
          ) : null}
        </div>
      </div>
      <div className="p-3">
        {m.top_values?.length ? (
          <div className="flex flex-wrap gap-1">
            {m.top_values.slice(0, 3).map((v) => (
              <span key={v} className="px-2 py-0.5 rounded-full text-[10px]"
                style={{ backgroundColor: "hsl(var(--muted))" }}
              >
                {optionLabel(lang, "values", v)}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Link>
  );
}

export default function Matches() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setItems(data.matches || []);
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-6xl mx-auto" data-testid="page-matches">
      <div>
        <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
          <Sparkles size={12} className="inline mr-1 -mt-0.5" /> {t("app.match.title")}
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-display">{t("app.match.title")}</h1>
        <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
          {t("app.match.subtitleList")}
        </p>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="matches-skeleton">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="nvl-card overflow-hidden animate-pulse">
                <div className="aspect-[4/5] w-full" style={{ backgroundColor: "hsl(var(--muted))" }} />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="nvl-card p-6" data-testid="matches-error">
            <p className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="nvl-card p-10 text-center" data-testid="matches-empty">
            <Heart size={22} style={{ color: "hsl(var(--nvl-accent))" }} className="mx-auto" />
            <h3 className="mt-4 font-display text-xl">{t("app.match.empty")}</h3>
            <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.match.emptyBody")}
            </p>
            <Link
              to="/app/discover"
              className="mt-5 inline-block nvl-btn nvl-btn-primary"
              data-testid="matches-discover-cta"
            >
              {t("app.match.keepDiscovering")}
            </Link>
          </div>
        ) : (
          <div
            className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            data-testid="matches-grid"
          >
            {items.map((m) => <MatchCard key={m.match_id} m={m} />)}
          </div>
        )}
      </div>
    </div>
  );
}
